'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/utils';

interface PopoverProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface PopoverTriggerProps {
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

interface PopoverContentProps {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
}

const PopoverContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}>({
  open: false,
  setOpen: () => {},
  triggerRef: { current: null },
});

const PopoverRoot: React.FC<PopoverProps> = ({ children, open: controlledOpen, onOpenChange }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef }}>
      <div className="relative inline-block">{children}</div>
    </PopoverContext.Provider>
  );
};

const PopoverTrigger: React.FC<PopoverTriggerProps> = ({ children, className, asChild }) => {
  const { open, setOpen, triggerRef } = React.useContext(PopoverContext);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      ref: (el: HTMLElement) => {
        triggerRef.current = el;
      },
      onClick: () => setOpen(!open),
    });
  }

  return (
    <button
      ref={(el) => {
        triggerRef.current = el;
      }}
      type="button"
      onClick={() => setOpen(!open)}
      className={cn(className)}
    >
      {children}
    </button>
  );
};

const PopoverContent: React.FC<PopoverContentProps> = ({
  children,
  className,
  align = 'center',
  side = 'bottom',
}) => {
  const { open, setOpen, triggerRef } = React.useContext(PopoverContext);
  const contentRef = useRef<HTMLDivElement>(null);
  const [montado, setMontado] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => setMontado(true), []);

  // Posiciona (fixed) a partir do gatilho e reposiciona ao rolar/redimensionar.
  useEffect(() => {
    if (!open) return;
    const atualizar = () => {
      if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    };
    atualizar();
    window.addEventListener('scroll', atualizar, true);
    window.addEventListener('resize', atualizar);
    return () => {
      window.removeEventListener('scroll', atualizar, true);
      window.removeEventListener('resize', atualizar);
    };
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        target instanceof Element &&
        target.closest(
          '[data-radix-popper-content-wrapper],[data-radix-select-content],[role="listbox"]',
        )
      ) {
        return;
      }
      if (
        contentRef.current &&
        !contentRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen, triggerRef]);

  if (!open || !montado || !rect) return null;

  const gap = 8;
  const transforms: string[] = [];
  let top = rect.bottom + gap;
  let left = rect.left;

  if (side === 'top') {
    top = rect.top - gap;
    transforms.push('translateY(-100%)');
  } else if (side === 'left') {
    top = rect.top;
    left = rect.left - gap;
    transforms.push('translateX(-100%)');
  } else if (side === 'right') {
    top = rect.top;
    left = rect.right + gap;
  }

  if (side === 'top' || side === 'bottom') {
    if (align === 'end') {
      left = rect.right;
      transforms.push('translateX(-100%)');
    } else if (align === 'center') {
      left = rect.left + rect.width / 2;
      transforms.push('translateX(-50%)');
    } else {
      left = rect.left;
    }
  }

  return createPortal(
    <div
      ref={contentRef}
      style={{ position: 'fixed', top, left, transform: transforms.join(' ') || undefined }}
      className={cn('z-50 rounded-md border border-gray-200 bg-white shadow-lg', className)}
    >
      {children}
    </div>,
    document.body,
  );
};

export const Popover = Object.assign(PopoverRoot, {
  Trigger: PopoverTrigger,
  Content: PopoverContent,
});
